import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Search, Edit, Building2, Save, Upload, FileSpreadsheet,
  ToggleLeft, ToggleRight, Percent, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { Textarea } from "@/components/ui/textarea";

const TIPOS_CONVENIO = [
  { value: 'NORMAL', label: 'Normal (Sin descuento)' },
  { value: 'DESCUENTO', label: 'Con Descuento %' },
  { value: 'PRECIO_FIJO', label: 'Precio Fijo' }
];

const EmpresasPage = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    rut: '',
    contacto_nombre: '',
    contacto_telefono: '',
    contacto_email: '',
    tipo_convenio: 'NORMAL',
    descuento_porcentaje: 0,
    precio_fijo: null,
    notas: ''
  });

  const fetchEmpresas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/empresas?active_only=${!showInactive}`, {
        withCredentials: true
      });
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error fetching empresas:', error);
      toast.error('Error al cargar las empresas');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/empresas/importar-excel`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        timeout: 600000,
      });
      setImportResult(r.data);
      toast.success('Importación completada');
      fetchEmpresas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };


  const handleOpenDialog = (empresa = null) => {
    if (empresa) {
      setEditingEmpresa(empresa);
      setFormData({
        nombre: empresa.nombre,
        rut: empresa.rut || '',
        contacto_nombre: empresa.contacto_nombre || '',
        contacto_telefono: empresa.contacto_telefono || '',
        contacto_email: empresa.contacto_email || '',
        tipo_convenio: empresa.tipo_convenio || 'NORMAL',
        descuento_porcentaje: empresa.descuento_porcentaje || 0,
        precio_fijo: empresa.precio_fijo,
        notas: empresa.notas || ''
      });
    } else {
      setEditingEmpresa(null);
      setFormData({
        nombre: '',
        rut: '',
        contacto_nombre: '',
        contacto_telefono: '',
        contacto_email: '',
        tipo_convenio: 'NORMAL',
        descuento_porcentaje: 0,
        precio_fijo: null,
        notas: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmpresa(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (editingEmpresa) {
        await axios.put(`${API}/empresas/${editingEmpresa.id}`, formData, {
          withCredentials: true
        });
        toast.success('Empresa actualizada');
      } else {
        await axios.post(`${API}/empresas`, formData, {
          withCredentials: true
        });
        toast.success('Empresa agregada');
      }
      handleCloseDialog();
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleActive = async (empresa) => {
    try {
      if (empresa.active) {
        await axios.delete(`${API}/empresas/${empresa.id}`, {
          withCredentials: true
        });
        toast.success('Empresa desactivada');
      } else {
        await axios.post(`${API}/empresas/${empresa.id}/activate`, {}, {
          withCredentials: true
        });
        toast.success('Empresa activada');
      }
      fetchEmpresas();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const filteredEmpresas = empresas.filter(e => 
    e.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.rut && e.rut.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (e.contacto_nombre && e.contacto_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTipoConvenioLabel = (tipo) => {
    const found = TIPOS_CONVENIO.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  const getConvenioInfo = (empresa) => {
    if (empresa.tipo_convenio === 'DESCUENTO') {
      return `${empresa.descuento_porcentaje}% descuento`;
    } else if (empresa.tipo_convenio === 'PRECIO_FIJO') {
      return `$${empresa.precio_fijo?.toLocaleString('es-CL') || 0} fijo`;
    }
    return 'Sin descuento';
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="empresas-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Empresas y Convenios</h1>
            <p className="text-slate-500 mt-1">
              Gestión de empresas con convenios de vacunación
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setImportOpen(true)}
              data-testid="import-empresas-btn"
            >
              <Upload className="w-4 h-4" />
              Importar Excel
            </Button>
            <Button 
              className="btn-accent gap-2" 
              onClick={() => handleOpenDialog()}
              data-testid="add-empresa-btn"
            >
              <Plus className="w-4 h-4" />
              Agregar Empresa
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, RUT o contacto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Button
                variant={showInactive ? "default" : "outline"}
                onClick={() => setShowInactive(!showInactive)}
                className="gap-2"
                data-testid="toggle-inactive-btn"
              >
                {showInactive ? (
                  <ToggleRight className="w-4 h-4" />
                ) : (
                  <ToggleLeft className="w-4 h-4" />
                )}
                {showInactive ? 'Mostrando todos' : 'Solo activos'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {empresas.filter(e => e.active).length}
                  </p>
                  <p className="text-sm text-slate-500">Empresas Activas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Percent className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {empresas.filter(e => e.tipo_convenio === 'DESCUENTO').length}
                  </p>
                  <p className="text-sm text-slate-500">Con Descuento</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {empresas.filter(e => e.tipo_convenio === 'PRECIO_FIJO').length}
                  </p>
                  <p className="text-sm text-slate-500">Precio Fijo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card data-testid="empresas-table-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Listado de Empresas ({filteredEmpresas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="table-wrapper overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="p-4 text-left">Empresa</th>
                    <th className="p-4 text-left">RUT</th>
                    <th className="p-4 text-left">Contacto</th>
                    <th className="p-4 text-left">Convenio</th>
                    <th className="p-4 text-center">Estado</th>
                    <th className="p-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center">
                        <div className="loading-spinner w-8 h-8 mx-auto"></div>
                      </td>
                    </tr>
                  ) : filteredEmpresas.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No hay empresas registradas</p>
                        <Button 
                          variant="link" 
                          className="mt-2"
                          onClick={() => handleOpenDialog()}
                        >
                          Agregar la primera
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    filteredEmpresas.map((empresa) => (
                      <tr key={empresa.id} className="table-row" data-testid={`empresa-row-${empresa.id}`}>
                        <td className="table-cell">
                          <div>
                            <p className="font-medium">{empresa.nombre}</p>
                            {empresa.notas && (
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{empresa.notas}</p>
                            )}
                          </div>
                        </td>
                        <td className="table-cell font-mono text-sm">
                          {empresa.rut || '-'}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm">
                            {empresa.contacto_nombre && <p>{empresa.contacto_nombre}</p>}
                            {empresa.contacto_telefono && (
                              <p className="text-slate-500">{empresa.contacto_telefono}</p>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <Badge 
                            variant="secondary" 
                            className={
                              empresa.tipo_convenio === 'DESCUENTO' 
                                ? 'bg-emerald-50 text-emerald-700'
                                : empresa.tipo_convenio === 'PRECIO_FIJO'
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-slate-50 text-slate-600'
                            }
                          >
                            {getConvenioInfo(empresa)}
                          </Badge>
                        </td>
                        <td className="table-cell text-center">
                          <Badge 
                            className={empresa.active 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-500'
                            }
                          >
                            {empresa.active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="table-cell text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(empresa)}
                              data-testid={`edit-btn-${empresa.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(empresa)}
                              className={empresa.active 
                                ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              }
                              data-testid={`toggle-btn-${empresa.id}`}
                            >
                              {empresa.active ? (
                                <ToggleRight className="w-4 h-4" />
                              ) : (
                                <ToggleLeft className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingEmpresa ? 'Editar Empresa' : 'Agregar Empresa'}
              </DialogTitle>
              <DialogDescription>
                {editingEmpresa 
                  ? 'Modifica los datos de la empresa' 
                  : 'Ingresa los datos de la nueva empresa o convenio'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="nombre">Nombre de Empresa *</Label>
                    <Input
                      id="nombre"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      placeholder="Ej: Empresa ABC Ltda."
                      data-testid="input-nombre"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rut">RUT Empresa</Label>
                    <Input
                      id="rut"
                      value={formData.rut}
                      onChange={(e) => setFormData({...formData, rut: e.target.value})}
                      placeholder="76.123.456-7"
                      data-testid="input-rut"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipo_convenio">Tipo de Convenio</Label>
                    <Select 
                      value={formData.tipo_convenio} 
                      onValueChange={(value) => setFormData({...formData, tipo_convenio: value})}
                    >
                      <SelectTrigger data-testid="select-tipo-convenio">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_CONVENIO.map(tipo => (
                          <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.tipo_convenio === 'DESCUENTO' && (
                  <div className="space-y-2">
                    <Label htmlFor="descuento">Porcentaje de Descuento</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="descuento"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.descuento_porcentaje}
                        onChange={(e) => setFormData({...formData, descuento_porcentaje: parseFloat(e.target.value) || 0})}
                        className="w-24"
                        data-testid="input-descuento"
                      />
                      <span className="text-slate-500">%</span>
                    </div>
                  </div>
                )}

                {formData.tipo_convenio === 'PRECIO_FIJO' && (
                  <div className="space-y-2">
                    <Label htmlFor="precio_fijo">Precio Fijo por Vacuna</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">$</span>
                      <Input
                        id="precio_fijo"
                        type="number"
                        min="0"
                        value={formData.precio_fijo || ''}
                        onChange={(e) => setFormData({...formData, precio_fijo: parseFloat(e.target.value) || null})}
                        className="w-32"
                        data-testid="input-precio-fijo"
                      />
                    </div>
                  </div>
                )}

                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Datos de Contacto</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="contacto_nombre">Nombre Contacto</Label>
                      <Input
                        id="contacto_nombre"
                        value={formData.contacto_nombre}
                        onChange={(e) => setFormData({...formData, contacto_nombre: e.target.value})}
                        placeholder="Nombre del contacto"
                        data-testid="input-contacto-nombre"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contacto_telefono">Teléfono</Label>
                      <Input
                        id="contacto_telefono"
                        value={formData.contacto_telefono}
                        onChange={(e) => setFormData({...formData, contacto_telefono: e.target.value})}
                        placeholder="+56 9 1234 5678"
                        data-testid="input-contacto-telefono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contacto_email">Email</Label>
                      <Input
                        id="contacto_email"
                        type="email"
                        value={formData.contacto_email}
                        onChange={(e) => setFormData({...formData, contacto_email: e.target.value})}
                        placeholder="contacto@empresa.cl"
                        data-testid="input-contacto-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notas">Notas</Label>
                  <Textarea
                    id="notas"
                    value={formData.notas}
                    onChange={(e) => setFormData({...formData, notas: e.target.value})}
                    placeholder="Notas adicionales sobre el convenio..."
                    className="min-h-[80px]"
                    data-testid="input-notas"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseDialog}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="gap-2" data-testid="save-btn">
                  <Save className="w-4 h-4" />
                  {editingEmpresa ? 'Actualizar' : 'Guardar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Excel Dialog */}
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Importar empresas desde Excel</DialogTitle>
              <DialogDescription>
                Columnas reconocidas: <strong>RUT, RAZON SOCIAL, DIRECCION, CIUDAD, COMUNA, TELEFONO, EMAIL, CONTACTO</strong>.
                Las empresas existentes (mismo RUT o nombre) se fusionan automáticamente: solo se llenan los campos vacíos.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {!importResult && (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 mb-3">Selecciona un archivo .xlsx</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => handleImport(e.target.files?.[0])}
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    data-testid="btn-select-empresas-excel"
                  >
                    {importing ? 'Procesando...' : 'Seleccionar archivo'}
                  </Button>
                </div>
              )}
              {importResult && (
                <div className="space-y-3" data-testid="empresas-import-result">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Nuevas</p>
                        <p className="text-xl font-bold text-emerald-700">{importResult.importados || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Fusionadas</p>
                        <p className="text-xl font-bold text-blue-700">{importResult.fusionados || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Saltadas</p>
                        <p className="text-xl font-bold text-slate-500">{importResult.saltados || 0}</p>
                      </div>
                    </div>
                  </div>
                  {importResult.errores?.length > 0 && (
                    <details className="text-xs text-slate-600">
                      <summary className="cursor-pointer">Ver {importResult.errores.length} errores</summary>
                      <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                        {importResult.errores.map((er, i) => (
                          <li key={`err-${i}`} className="text-rose-600">{er}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setImportOpen(false); setImportResult(null); }}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EmpresasPage;
